import React, { useState, Suspense, lazy, useMemo } from 'react';
import { X, Settings } from 'lucide-react';
import { IconButton, LoadingSpinner } from '../ui';
import { SystemConfigTab } from './SystemConfigTab';
import { TasteProfileSection } from '../TasteProfileSection';
import { BulkRunsTab } from './BulkRunsTab';
import { AlbumSessionsTab } from './AlbumSessionsTab';
import { useAuth } from '@/context/AuthContext';

// Lazy load heavy admin tabs for code splitting
const AutopsyTab = lazy(() => import('./AutopsyTab').then(m => ({ default: m.AutopsyTab })));
const BandBuilderTab = lazy(() => import('./BandBuilderTab').then(m => ({ default: m.BandBuilderTab })));
const VocalForgeImportTab = lazy(() => import('./VocalForgeImportTab').then(m => ({ default: m.VocalForgeImportTab })));
const InstrumentForgeImportTab = lazy(() => import('./InstrumentForgeImportTab').then(m => ({ default: m.InstrumentForgeImportTab })));
const SoloForgeImportTab = lazy(() => import('./SoloForgeImportTab').then(m => ({ default: m.SoloForgeImportTab })));
const MixForgeGeneratorTab = lazy(() => import('./MixForgeGeneratorTab').then(m => ({ default: m.MixForgeGeneratorTab })));
const GenreForgeGeneratorTab = lazy(() => import('./GenreForgeGeneratorTab').then(m => ({ default: m.GenreForgeGeneratorTab })));
const SeedForgeGeneratorTab = lazy(() => import('./SeedForgeGeneratorTab').then(m => ({ default: m.SeedForgeGeneratorTab })));
const DatabaseManagerTab = lazy(() => import('./DatabaseManagerTab').then(m => ({ default: m.DatabaseManagerTab })));
const StylePresetManagerTab = lazy(() => import('./StylePresetManagerTab').then(m => ({ default: m.StylePresetManagerTab })));
const SongTopicManagerTab = lazy(() => import('./SongTopicManagerTab').then(m => ({ default: m.SongTopicManagerTab })));
const ServerControlTab = lazy(() => import('./ServerControlTab').then(m => ({ default: m.ServerControlTab })));
const CostControlTab = lazy(() => import('./CostControl/CostControlTab').then(m => ({ default: m.CostControlTab })));
const StorageManagerTab = lazy(() => import('./StorageManager/StorageManagerTab').then(m => ({ default: m.StorageManagerTab })));
const ApiHealthTab = lazy(() => import('./ApiHealthTab').then(m => ({ default: m.ApiHealthTab })));
const NgrokTunnelTab = lazy(() => import('./NgrokTunnelTab').then(m => ({ default: m.NgrokTunnelTab })));

interface AdminPageProps {
  isOpen: boolean;
  onClose: () => void;
  sunoCookie: string;
  onSave: (settings: { sunoCookie: string; sunoProxyUrl: string }) => void;
  onVocalPacksUpdated?: () => void;
}

type AdminTab = 'system' | 'taste' | 'bulk' | 'album-session' | 'apihealth' | 'server' | 'ngrok' | 'database' | 'storage' | 'autopsy' | 'bandbuilder' | 'vocalforge' | 'instrumentforge' | 'soloforge' | 'mixforge' | 'genreforge' | 'seedforge' | 'stylepresets' | 'songtopics' | 'costs';

interface TabConfig {
  id: AdminTab;
  label: string;
  group: string;
  adminOnly?: boolean;
}

const TAB_GROUPS: Array<{ label: string; tabs: TabConfig[] }> = [
  {
    label: 'YOU',
    tabs: [
      { id: 'taste', label: 'Taste Profile', group: 'YOU' },
      { id: 'bulk', label: 'Bulk Runs', group: 'YOU' },
      { id: 'album-session', label: 'Album Session', group: 'YOU' },
    ],
  },
  {
    label: 'SERVER',
    tabs: [
      { id: 'system', label: 'SysConfig', group: 'SERVER' },
      { id: 'apihealth', label: 'Health', group: 'SERVER' },
      { id: 'server', label: 'ServerOps', group: 'SERVER', adminOnly: true },
      { id: 'ngrok', label: 'Tunnel', group: 'SERVER', adminOnly: true },
    ],
  },
  {
    label: 'DATA',
    tabs: [
      { id: 'database', label: 'Database', group: 'DATA', adminOnly: true },
      { id: 'storage', label: 'Storage', group: 'DATA', adminOnly: true },
    ],
  },
  {
    label: 'FORGE',
    tabs: [
      { id: 'autopsy', label: 'Autopsy', group: 'FORGE' },
      { id: 'bandbuilder', label: 'Band Builder', group: 'FORGE' },
      { id: 'vocalforge', label: 'Vocal', group: 'FORGE' },
      { id: 'instrumentforge', label: 'Instrument', group: 'FORGE' },
      { id: 'soloforge', label: 'Solo', group: 'FORGE' },
      { id: 'mixforge', label: 'Mix', group: 'FORGE' },
      { id: 'genreforge', label: 'Genre', group: 'FORGE' },
      { id: 'seedforge', label: 'Seed', group: 'FORGE' },
      { id: 'stylepresets', label: 'Presets', group: 'FORGE' },
      { id: 'songtopics', label: 'Topics', group: 'FORGE' },
    ],
  },
  {
    label: 'MONITORING',
    tabs: [
      { id: 'costs', label: 'Cost Control', group: 'MONITORING', adminOnly: true },
    ],
  },
];

function TabLoadingFallback(): React.ReactElement {
  return (
    <div className="flex items-center justify-center py-20">
      <LoadingSpinner size="lg" label="Loading module..." />
    </div>
  );
}

function TabButton({ tab, isActive, onClick }: { tab: TabConfig; isActive: boolean; onClick: () => void }): React.ReactElement {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-3 text-[11px] font-black uppercase tracking-widest flex-shrink-0 transition-all border-b-2 ${
        isActive
          ? 'bg-slate-950 border-accent text-accent'
          : 'border-transparent text-slate-400 hover:text-slate-300 hover:bg-slate-900/50'
      }`}
    >
      {tab.label}
    </button>
  );
}

export function AdminPage({ isOpen, onClose, sunoCookie, onSave, onVocalPacksUpdated }: AdminPageProps): React.ReactElement | null {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>('system');

  // Filter tab groups to only show tabs the user has access to
  const visibleGroups = useMemo(() => {
    return TAB_GROUPS
      .map(group => ({
        ...group,
        tabs: group.tabs.filter(tab => !tab.adminOnly || isAdmin),
      }))
      .filter(group => group.tabs.length > 0);
  }, [isAdmin]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-950 z-[100] flex flex-col">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center shadow-lg shadow-accent/20">
            <Settings className="text-slate-950" size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-50 tracking-tight">Admin Panel</h1>
            <p className="text-[11px] text-slate-400 font-bold uppercase tracking-[0.2em]">System Configuration & Management</p>
          </div>
        </div>
        <IconButton
          variant="ghost"
          size="md"
          onClick={onClose}
          aria-label="Close admin panel"
        >
          <X size={24} />
        </IconButton>
      </header>

      {/* Tab Navigation */}
      <nav className="bg-slate-900/50 border-b border-slate-800 px-6">
        <div className="flex items-end">
          {visibleGroups.map((group, gi) => (
            <React.Fragment key={group.label}>
              {gi > 0 && <div className="w-px h-8 bg-slate-700 mx-1.5 self-center flex-shrink-0" />}
              <div className="flex flex-col flex-shrink-0">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] px-3 mb-0.5">
                  {group.label}
                </span>
                <div className="flex">
                  {group.tabs.map((tab) => (
                    <TabButton
                      key={tab.id}
                      tab={tab}
                      isActive={activeTab === tab.id}
                      onClick={() => setActiveTab(tab.id)}
                    />
                  ))}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </nav>

      {/* Tab Content */}
      <main className="flex-1 overflow-y-auto p-6">
        <div className={activeTab === 'database' || activeTab === 'storage' || activeTab === 'server' || activeTab === 'costs' || activeTab === 'apihealth' || activeTab === 'ngrok' || activeTab === 'stylepresets' || activeTab === 'songtopics' ? 'w-full' : 'max-w-5xl mx-auto'}>
          {activeTab === 'system' && (
            <SystemConfigTab
              sunoCookie={sunoCookie}
              onSave={onSave}
              onClose={onClose}
            />
          )}
          {activeTab === 'taste' && (
            <TasteProfileSection />
          )}
          {activeTab === 'bulk' && (
            <BulkRunsTab />
          )}
          {activeTab === 'album-session' && (
            <AlbumSessionsTab />
          )}
          <Suspense fallback={<TabLoadingFallback />}>
            {activeTab === 'apihealth' && (
              <ApiHealthTab />
            )}
            {activeTab === 'costs' && (
              <CostControlTab />
            )}
            {activeTab === 'autopsy' && <AutopsyTab />}
            {activeTab === 'bandbuilder' && <BandBuilderTab />}
            {activeTab === 'vocalforge' && (
              <VocalForgeImportTab
                onProfileInjected={onVocalPacksUpdated}
              />
            )}
            {activeTab === 'instrumentforge' && (
              <InstrumentForgeImportTab
                onProfileInjected={onVocalPacksUpdated}
              />
            )}
            {activeTab === 'soloforge' && (
              <SoloForgeImportTab
                onProfileInjected={onVocalPacksUpdated}
              />
            )}
            {activeTab === 'mixforge' && (
              <MixForgeGeneratorTab />
            )}
            {activeTab === 'genreforge' && (
              <GenreForgeGeneratorTab />
            )}
            {activeTab === 'seedforge' && (
              <SeedForgeGeneratorTab />
            )}
            {activeTab === 'stylepresets' && (
              <StylePresetManagerTab />
            )}
            {activeTab === 'songtopics' && (
              <SongTopicManagerTab />
            )}
            {activeTab === 'database' && (
              <DatabaseManagerTab />
            )}
            {activeTab === 'storage' && (
              <StorageManagerTab />
            )}
            {activeTab === 'server' && (
              <ServerControlTab />
            )}
            {activeTab === 'ngrok' && (
              <NgrokTunnelTab />
            )}
          </Suspense>
        </div>
      </main>
    </div>
  );
}
